/**
 * Why an existing AR invoice opens READ-ONLY in `CreateARInvoiceForm` — the
 * one rule both editor hosts (`ARInvoicesTab`, `EditARInvoiceDialog`) and
 * the form itself read, so "which invoices can't be saved" lives in one place.
 *
 * - `fund`: a fund installment (`source === 'fund'`, `<fund_code>/<n>`) is
 *   posted and owned by its fund — the manual AR write RPCs refuse it
 *   (`FUND_MANAGED`). Takes precedence: it is structural, and the fix is
 *   always "manage it from the fund".
 * - `cancelled`: a cancelled invoice (post-on-issue, 20261101120000) is a
 *   closed document — the update RPC refuses it (`CANCELLED`).
 * - `legacy_posted`: a draft still carrying a posting from before
 *   post-on-issue — refused (`LEGACY_POSTED`) until the repair has run.
 * - `declared`: a VAT line on a filed return — the update RPC re-posts every
 *   line and the declared-VAT guard refuses it; AR has no header-only save
 *   (docs/TAX.md "Edit guard" → *The AR exception*).
 * - `filed_period`: an ISSUED invoice dated inside a filed VAT period with no
 *   declared line (a 0% / exempt / reverse-charge invoice carries no
 *   settlement tag) — the update RPC refuses any issued edit there
 *   (`VAT_PERIOD_FILED`). The fact is `ar_invoice_index.in_filed_vat_period`;
 *   the caller passes it (`useARInvoiceEditability` reads it). The
 *   correction is a cancel + a new invoice; the FUND alone moves through the
 *   panel's "Change fund" action (`update_ar_invoice_fund`).
 * - `paid`: an ISSUED invoice with money received — its amounts can no
 *   longer be re-posted in place (`HAS_PAYMENTS`); correct it with a credit
 *   note and a new invoice.
 *
 * A read-only invoice renders every input disabled under a banner naming the
 * reason, and the host withholds submit. Payments are unaffected, and so is
 * the panel's "Change fund" action on an issued, unpaid declared /
 * filed-period invoice (`arInvoiceActionGates().changeFund`).
 */
import type { ARInvoice } from '@/hooks/useAccountsReceivable';
import { declaredSettlementIdOf } from '@/lib/vatDeclared';
import { arHasReceipts, isARIssued, isLegacyPostedDraft } from '@/lib/arInvoiceLifecycle';

export type ARInvoiceReadOnlyReason =
  | { kind: 'fund'; fundId: string | null }
  | { kind: 'cancelled' }
  | { kind: 'legacy_posted' }
  | { kind: 'declared'; settlementId: string }
  | { kind: 'filed_period'; invoiceDate: string }
  | { kind: 'paid' };

export function arInvoiceReadOnlyReason(
  invoice:
    | (Pick<ARInvoice, 'source' | 'ledger_transaction'> &
        Partial<Pick<ARInvoice, 'status' | 'ledger_transaction_id' | 'amount_received' | 'invoice_date'>>)
    | null
    | undefined,
  /** `ar_invoice_index.in_filed_vat_period` of this invoice (false / absent = not known to be). */
  inFiledVatPeriod = false,
): ARInvoiceReadOnlyReason | null {
  if (!invoice) return null;
  if (invoice.source === 'fund') {
    return { kind: 'fund', fundId: invoice.ledger_transaction?.fund_id ?? null };
  }
  if (invoice.status === 'cancelled') return { kind: 'cancelled' };
  if (isLegacyPostedDraft({ status: invoice.status, ledger_transaction_id: invoice.ledger_transaction_id })) {
    return { kind: 'legacy_posted' };
  }
  const settlementId = declaredSettlementIdOf(invoice.ledger_transaction?.entries);
  if (settlementId) return { kind: 'declared', settlementId };
  const issued = !!invoice.status && isARIssued({ status: invoice.status });
  if (issued && inFiledVatPeriod && invoice.invoice_date) {
    return { kind: 'filed_period', invoiceDate: invoice.invoice_date.slice(0, 10) };
  }
  if (issued && arHasReceipts(invoice)) {
    return { kind: 'paid' };
  }
  return null;
}
